import type {
  PermissionCatalogEntry,
  PermissionSlug,
} from '../../services/userPermissionsService';

/**
 * THE COPY DECK for the permission editor — every sentence the screen says about
 * what a switch actually does (design §C).
 *
 * WHY IT IS HERE AND NOT ON THE WIRE: `GET /permissions/catalog` serves only facts
 * a spec can weld (labels, categories, four booleans, the role defaults). Prose is
 * not one of those, and putting it behind a backend deploy plus an openapi regen
 * would make every re-word a backend PR for copy no guard reads. Ali re-words HERE.
 *
 * ⚠ THE ONE RULE (INV-106): every sentence must be one the system makes true.
 *   - never "removes from the device" — a revoke narrows FUTURE pulls only
 *     (`sync-permissions.ts` omits the key; nothing deletes a replica row);
 *   - never "Owners and Admins" where the code says Owner;
 *   - never present a not-enforced slug as doing something. Since #85
 *     (2026-09-21) that is enforced by HIDING the row rather than by a sentence:
 *     a catalog entry whose `notEnforced` is true is not rendered at all
 *     (`permissionEditorModel.visibleCategories`), so there is no dead control
 *     for a note to sit under and no "not enforced yet" sentence in this file;
 *   - check the /sync entity table (`SYNC_ENTITY_PERMISSIONS`) as well as the
 *     controller before saying a slug does NOT gate something. The two planes
 *     disagree on purpose (`sync-permissions.ts:49`), so an exemption read off the
 *     REST decorators alone is false for the offline app — `manage:appointments`
 *     and shared calendar blocks is exactly that case.
 *
 * ⚠ SECOND RULE, #86 (Ali, 2026-09-21): NO EM DASH AND NO EN DASH in any sentence
 * below. A "—" in copy a clinic reads looks machine-written. Rewrite the aside as
 * a second short sentence, or join it with "because" / "so" / a comma — never swap
 * the dash for a hyphen, which keeps the shape the rule is about. Comments in this
 * file are exempt: nobody outside the repo reads them. `permissionNotes.test.ts`
 * walks every VALUE here (and the other exported sentences on this surface) and
 * fails on either character.
 * The evidence for each sentence below is the per-line comment, and the sentences
 * THEMSELVES are reviewed by `permissionNotes.test.ts`, which pins the ones that
 * would break the rule most sharply against the controllers they describe.
 * ⚠ The copy table in `git show a9d75b38:Docs/planning/plans/permissions-p6-PLAN.md` (Task 2) was the
 * ORIGINAL DRAFT, not a current artifact: the P6 final review retired three of its
 * sentences as claims the code refutes (`manage:appointments`' calendar-block
 * exemption, `manage:integrations`' connect/disconnect, `manage:practice_settings`'
 * one-door list). The plan is kept as the historical record it is — this deck and
 * its per-slug tests supersede it, so re-word against the CODE, never against that
 * table.
 *
 * `Record<PermissionSlug, string | null>` is TOTAL: slug #31 cannot ship without
 * a decision here, and `null` is a real decision ("nothing true is worth saying").
 * CRA's jest does not type-check, so the runtime half of that promise is the
 * drift guard in the suite.
 */

/** The six `view:` slugs the offline plane reads. NARROWS future pulls; never wipes. */
const OFFLINE_VIEW_NOTE =
  'Turning this off stops new records of this type from reaching their offline copy. ' +
  'Records already downloaded stay on their device.';

/** `POST /ai/command` requires all three together, so revoking any one closes it. */
const AI_COMMAND_NOTE =
  "The assistant's quick commands need Manage appointments, Manage charges and " +
  'Manage patients together.';

export const PERMISSION_NOTES: Record<PermissionSlug, string | null> = {
  // Settings.tsx:78-80 renders the Team tab iff this slug is held, and
  // users.controller.ts:169-170 gates GET /users on it.
  'manage:users': 'Also controls the Settings > Team screen and the team list it shows.',
  // The catalog couples this to manage:users (design §B, #74): without it there is
  // no route to this screen at all.
  'manage:roles_permissions': 'Needs Manage users, which is how this screen is reached.',

  // sync-permissions.ts: `horse` rides view:patients.
  'view:patients': OFFLINE_VIEW_NOTE,
  'manage:patients': AI_COMMAND_NOTE,
  // sync-permissions.ts:69-100: twelve offline entity rows ride this ONE slug.
  'view:medical_records':
    OFFLINE_VIEW_NOTE +
    ' This also withholds medications, vaccinations, procedures and discharge ' +
    'summaries from their offline copy, whatever their own switches say.',
  'manage:medical_records': null,
  'manage:medical_records_finalize': null,
  'manage:medical_records_archive': null,
  // vaccination-lot-patch.ts assertExpiredLotPermitted: REST, Barn Operations and /sync all refuse
  // without it. The iPhone sends no lot, so it never meets the gate.
  'manage:expired_lot_doses':
    'Lets this person record a vaccine dose from a lot that had expired by the day it was given. ' +
    'Without it they can still pick another lot, or save the dose without one.',

  'view:clients': OFFLINE_VIEW_NOTE,
  'manage:clients': null,

  'view:appointments': OFFLINE_VIEW_NOTE,
  // BOTH PLANES: time-blocks.controller.ts carries no permission gate and no role gate at
  // all, but sync-permissions.ts:124 maps `timeBlock` manage to THIS slug and
  // sync.service.ts:1543 authorizes every pushed op, so a revoke refuses the offline app's
  // time-block writes. The transport difference is deliberate (sync-permissions.ts:49).
  'manage:appointments':
    AI_COMMAND_NOTE +
    ' Does not restrict shared calendar blocks on the web. The offline app still needs it ' +
    'to create or change them.',

  'view:billing': OFFLINE_VIEW_NOTE,
  // billing.controller.ts:104-105 / :294-295 — the invoice create+update routes
  // carry the discount fields and are gated on THIS slug, not on manage:discounts.
  'manage:charges':
    AI_COMMAND_NOTE +
    ' Invoice and line-item discounts are part of this, not of Apply discounts.',
  'manage:payments': null,
  // credits.controller.ts:15-18 is the only door this slug opens.
  'manage:discounts':
    'Controls issuing account credits. Invoice and line-item discounts are part of ' +
    'Manage charges.',
  // billing.service.ts:2936, payments.service.ts:2976 and :3297 all call
  // verifyApprover UNCONDITIONALLY, so there is no exemption for the person starting
  // it. approval.service.ts:27-30 requires an ACTIVE ADMIN/OWNER of the same clinic
  // and never compares the approver with the actor, so it is not a second PERSON
  // either — an Owner or Admin who holds a PIN may approve their own.
  'manage:refunds':
    "Refunds and voids always ask for an Owner's or Admin's approval PIN. An Owner " +
    'or Admin can enter their own; anyone else needs one of them to approve.',
  // ── Reports ──────────────────────────────────────────────────────────────
  // reports.controller.ts carried a CLASS-level @Roles(OWNER) until 2026-09-21, which made
  // `view:financial_reports` override-INERT there. ADR-0035 A8 removed it: every handler now
  // declares `view:reports` plus its own data slug, so BOTH switches below are live.
  // ⚠ TWO SENTENCES EACH, and the second one carries the THIRD slug. The conjunctions differ
  // WITHIN the money: revenue and the KPI tiles are `view:reports` + `view:financial_reports`,
  // while every `reports/financial` route — receivables, production by vet — keeps `view:billing`
  // as a third term, because it reads the invoice and payment rows that slug has always gated.
  // A note that stopped at "See financial reports" sent an owner to grant one switch and file a
  // bug about the receivables panels that stayed missing.
  // ⚠ RE-WORDED 2026-09-21 (#86): four plain sentences where two em-dash asides were. Every
  // clause is the same claim it was; only the punctuation moved.
  'view:reports':
    'Opens the Reports page. What they see inside follows their other permissions. ' +
    'Financial reports also need "See financial reports", which Admins, Veterinarians and ' +
    'Technicians have on by default. Receivables and production by vet need "View billing" too.',
  // Two doors on one slug: the financial routes of reports.controller.ts (in conjunction with
  // view:reports), and get-accounts-receivable.tool.ts:66, which has no role gate and is NOT
  // coupled to view:reports — which is why REQUIRES_VIEW leaves this slug uncoupled.
  'view:financial_reports':
    'Shows revenue on the Reports page, for a person who can open it, and controls the ' +
    "assistant's accounts-receivable answers. Receivables and production by vet need " +
    '"View billing" on top of it.',
  // service-catalog.controller.ts:20-22, :46-48, :61-63, :69-71.
  'manage:pricing': 'Editing the service price list also needs the Owner or Admin role.',

  'view:inventory': OFFLINE_VIEW_NOTE,
  'manage:inventory': null,
  // notEnforced at HEAD — `noteFor` supplies NOT_ENFORCED_NOTE from the catalog flag.
  'manage:inventory_adjustments': null,
  'manage:inventory_pricing': null,
  'manage:controlled_substances': null,

  // discharge-summaries.controller.ts:305 — the P5 re-point (Ali #58) made this the
  // gate on the email half, which is the front desk's one discharge door.
  'view:discharge_summaries': 'Also controls emailing a finished discharge summary to an owner.',
  'manage:discharge_summaries': null,

  // clinics.controller.ts:52-54, uploads.controller.ts:25-27 and :52-54,
  // payment-types.controller.ts:36-56 and billing.controller.ts:267-269 all pair the slug
  // with @Roles('OWNER','ADMIN'). THREE doors carry the slug and NO @Roles, so a technician
  // holding it reaches all three: feedback.controller.ts:31-32 (GET /feedback),
  // clinics.controller.ts:32-33 (GET /clinics) and :41-42 (GET /clinics/:id). Naming only
  // the feedback list read as exclusive and quietly promised the other two need a role —
  // which is why the gateless set is named as a SET and the enumeration above must not read
  // as an Owner/Admin-only whole.
  'manage:practice_settings':
    'Editing clinic details, the clinic logo, the payment-type list and statement ' +
    'settings also needs the Owner or Admin role. The clinic feedback list and ' +
    'reading the clinic details ride this switch alone.',
  // sync-permissions.ts:126-129 — soapTemplate's offline READ rides
  // view:medical_records. ⚠ Claims NO role: soap-notes.controller.ts:102-119 carry
  // this slug and no @Roles, whatever permissions.ts:299's stale comment says.
  'manage:clinical_settings':
    'SOAP templates reach a phone or tablet through View medical records, not ' +
    'through this switch.',
  // The slug rides exactly TWO doors: quickbooks.controller.ts:136 (sync-log retry) and
  // stripe-connect.controller.ts:162 (dashboard-link) — the two whose service methods do
  // NOT re-check the role, which is why a permission gate there binds. Connect/disconnect
  // carry the slug NOWHERE: they are pure @Roles (quickbooks :73 OWNER/ADMIN, :118 OWNER;
  // stripe-connect :51/:68/:89 OWNER/ADMIN, :148 OWNER), so this switch closes nothing
  // there. idexx.controller.ts:83 and keystone.controller.ts carry no @Roles at all.
  // ⚠ RE-WORDED 2026-09-21 (#86): the em-dash aside that carried the two role facts is its own
  // sentence now. Polarity unchanged: connect is OWNER/ADMIN, disconnect is OWNER.
  'manage:integrations':
    'Controls the QuickBooks sync-log retry and the Stripe dashboard link. Connecting or ' +
    'disconnecting QuickBooks and Stripe is limited by role, not by this switch. Connecting ' +
    'needs an Owner or Admin, and disconnecting needs the Owner. Lab and imaging integration ' +
    'settings ride this switch alone.',

  // Enforced service-side against the DB ROLE (subscription.service.ts:64, :169, :282, :1100),
  // so an Admin cannot be granted it. ⚠ The row is HIDDEN since #85, so this sentence is not on
  // screen today; it is kept, and kept TRUE, because it is a fact about the system rather than
  // about the control, and it is what the row would say the day the slug gains a gate. The
  // clause about the switch itself went with the switch.
  'manage:subscription': 'Billing for your StableTrack plan is always limited to the clinic Owner.',
};

/**
 * The sentence a row shows, given its catalog entry. A PURE LOOKUP since #85.
 *
 * It used to rank an inert sentence above the capability note, so a slug that became inert was
 * labelled inert the day the catalog said so. That ranking existed to stop a dead control
 * advertising a capability — and #85 closes the same hazard at the source, by not rendering the
 * row at all (`permissionEditorModel.visibleCategories`). The flag therefore decides VISIBILITY
 * and never the words; `noteFor` keeps its entry parameter because the catalog row is what the
 * caller holds, and because the next honesty rule that needs a flag will land here.
 */
export function noteFor(entry: PermissionCatalogEntry): string | null {
  return PERMISSION_NOTES[entry.slug];
}
